import { Link } from "react-router-dom";
import { topics, formatHeat } from "@/mocks/topics";
import { site } from "@/mocks/site";
import TrendBadge from "@/components/base/TrendBadge";

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function HeroSection() {
  const featured = topics[0];
  const totalHeat = topics.reduce((sum, item) => sum + item.heat, 0);

  return (
    <section id="top" className="w-full border-b border-background-200 bg-background-100">
      <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12 lg:gap-12">
        <div className="cg-fade-up flex flex-col justify-center lg:col-span-7">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-label font-medium text-primary-700">
            <span className="flex h-1.5 w-1.5 items-center justify-center rounded-full bg-primary-500"></span>
            实时榜单 · 更新于 {featured.updatedAt}
          </span>

          <h1 className="mt-5 font-heading text-4xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl lg:text-6xl">
            今日瓜榜
            <span className="mt-2 block text-2xl font-bold text-primary-600 md:text-3xl">
              {site.slogan}
            </span>
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground-600">
            {site.name} 汇总全网正在被围观的焦点事件，按讨论量与增速实时排名。打开榜单，一眼看清今天大家都在聊什么，不用来回翻十几个平台。
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() => scrollTo("rank")}
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-fire-fill"></i>
              查看完整总榜
            </button>
            <button
              type="button"
              onClick={() => scrollTo("channels")}
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-label font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-layout-grid-line"></i>
              按分类浏览
            </button>
          </div>

          <dl className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <dt className="text-xs font-label text-foreground-500">当前榜单条目</dt>
              <dd className="mt-1 font-heading text-2xl font-bold text-foreground-950">
                128<span className="ml-1 text-sm font-normal text-foreground-500">条</span>
              </dd>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <dt className="text-xs font-label text-foreground-500">今日总热度</dt>
              <dd className="mt-1 font-heading text-2xl font-bold text-primary-600">
                {formatHeat(totalHeat)}
              </dd>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <dt className="text-xs font-label text-foreground-500">平均更新间隔</dt>
              <dd className="mt-1 font-heading text-2xl font-bold text-foreground-950">
                15<span className="ml-1 text-sm font-normal text-foreground-500">分钟</span>
              </dd>
            </div>
          </dl>
        </div>

        <div className="lg:col-span-5">
          <article data-product-shop className="cg-fade-up overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="relative h-56 w-full md:h-64">
              <img
                src={featured.cover}
                alt="今日吃瓜榜首名话题配图"
                title={`${featured.title} - 51吃瓜榜今日焦点排行`}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
              <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-label font-bold text-background-50">
                <i className="ri-trophy-fill"></i>
                榜单第 1 名
              </span>
              {featured.live && (
                <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-md bg-background-50/95 px-2.5 py-1 text-xs font-label font-medium text-primary-700">
                  <span className="flex h-1.5 w-1.5 items-center justify-center rounded-full bg-primary-500"></span>
                  实时
                </span>
              )}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex flex-wrap items-center gap-2">
                  <TrendBadge trend={featured.trend} value={featured.trendValue} />
                  <span className="inline-flex items-center gap-1 rounded-full bg-foreground-950/60 px-2 py-0.5 text-xs font-label text-background-50">
                    <i className="ri-chat-3-line"></i>
                    {featured.comments} 条讨论
                  </span>
                </div>
              </div>
            </div>
            <div className="p-5">
              <h2 className="font-heading text-lg font-bold leading-snug text-foreground-950">
                {featured.title}
              </h2>
              <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{featured.summary}</p>
              <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4">
                <span className="flex items-center gap-1.5 font-label text-sm font-semibold text-primary-600">
                  <i className="ri-fire-line"></i>
                  热度 {formatHeat(featured.heat)}
                </span>
                <Link
                  to={`/topic/${featured.id}`}
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-label font-medium text-foreground-800 transition-colors hover:text-primary-600"
                >
                  看详情
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}